import React, { useState } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import ReactJson from 'react-json-view';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';

function FdiPackageSearch({ apiBaseUrl }) {
  const { instance, accounts } = useMsal();
  const [packageId, setPackageId] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const getAccessToken = async () => {
    if (accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/.default'],
        account: accounts[0],
      });
      const decoded = jwtDecode(response.accessToken);
      console.log('Token type:', response.tokenType, 'Audience:', decoded.aud);
      return response.accessToken;
    } catch (err) {
      console.error('Error acquiring token:', err);
      instance.acquireTokenRedirect({ scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/.default'] });
      return null;
    }
  };

  const handleSearch = async () => {
    if (!packageId) {
      setError('Please enter a Package ID to search.');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setExpandedRow(null);

    try {
      const token = await getAccessToken();
      if (!token) return;

      const url = `${apiBaseUrl}/packages/package/${encodeURIComponent(packageId)}`;
      console.log('Sending package search request:', {
        url,
        method: 'GET',
        headers: { Authorization: `Bearer ${token.substring(0, 20)}...` },
      });

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      console.log('Package search response:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data,
      });

      setResults(response.data.entries || []);
    } catch (err) {
      console.error('Package search error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        headers: err.response?.headers,
        data: err.response?.data,
      });

      let errorMessage = 'An error occurred while searching for the package.';
      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMessage = 'Bad Request: Invalid Package ID format.';
            break;
          case 401:
            errorMessage = 'Unauthorized: Please log in again.';
            break;
          case 403:
            errorMessage = 'Forbidden: Insufficient permissions to search packages.';
            break;
          case 404:
            errorMessage = `Package ID "${packageId}" not found.`;
            break;
          case 500:
            errorMessage = 'Server Error: Try again later.';
            break;
          default:
            errorMessage = `Error ${err.response.status}: ${err.response.statusText}`;
        }
        if (err.response.data?.fault?.faultstring) {
          errorMessage += ` - ${err.response.data.fault.faultstring}`;
        }
      } else if (err.request) {
        errorMessage = 'Network Error: Check your connection or the API server.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (url) => {
    try {
      setLoading(true);
      console.log('Downloading from URL:', url);

      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      console.log('Download response status:', response.status);
      console.log('Final URL:', response.request.responseURL);

      const finalUrl = response.request.responseURL || url;
      const urlParts = finalUrl.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0] || 'package_file';

      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download Error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        headers: err.response?.headers,
      });
      setError('Failed to download file. Check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Package Search
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <TextField
          label="Package ID"
          value={packageId}
          onChange={(e) => setPackageId(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ mb: 2, width: 600 }}
          disabled={loading}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          Search
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          <AlertTitle>Error</AlertTitle>
          {error} — <strong>Check the console for detailed logs.</strong>
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : results.length === 0 && !error ? (
        <Typography>No results to display. Enter a Package ID to search.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table sx={{ tableLayout: 'auto', minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Download</TableCell>
                <TableCell>Update Time</TableCell>
                <TableCell>Package ID</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Device Type Names</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((item, index) => (
                <React.Fragment key={index}>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleDownload(item.url)}
                        disabled={loading}
                      >
                        Download
                      </Button>
                    </TableCell>
                    <TableCell>{item.updateTime}</TableCell>
                    <TableCell>{item.catalog?.packageId || 'N/A'}</TableCell>
                    <TableCell>{item.catalog?.manufacturerName || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                      {item.catalog?.listOfDeviceTypes
                        ?.map((deviceTypeObj) => deviceTypeObj.deviceType?.name || 'N/A')
                        .join(', ') || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => toggleDetails(index)}
                        disabled={loading}
                      >
                        {expandedRow === index ? 'Hide Details' : 'Show Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedRow === index && (
                    <TableRow>
                      <TableCell colSpan={6} style={{ backgroundColor: '#1e1e1e', padding: '16px' }}>
                        <Typography variant="h6" gutterBottom color="white">
                          Package Details
                        </Typography>
                        <ReactJson
                          src={item.catalog}
                          theme="monokai"
                          collapsed={false}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          style={{ backgroundColor: '#1e1e1e' }}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default FdiPackageSearch;